import React, { useCallback, useEffect, useRef, useState } from 'react'

export type DropdownMenuProps = {
  onChangeHandler: (item: string) => void
  items?: string[]
  currentItem: string
  summaryBeforeText: string
  summaryAfterText: string
  testId?: string | false
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  onChangeHandler,
  items = [],
  currentItem,
  summaryBeforeText,
  summaryAfterText,
  testId = false
}) => {
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState<number | undefined>(undefined)
  const menuRef = useRef<HTMLUListElement | null>(null)
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([])
  const dropdownRef = useRef<HTMLButtonElement | null>(null)

  const handleArrowUp = useCallback(() => {
    if (focusIndex === 0) {
      return
    }
    if (focusIndex === undefined) {
      itemsRef.current[0]?.focus()
      setFocusIndex(0)
      return
    }
    itemsRef.current[focusIndex]?.focus()
    itemsRef.current[focusIndex - 1]?.focus()
    setFocusIndex(focusIndex - 1)
  }, [focusIndex])

  const handleArrowDown = useCallback(() => {
    if (focusIndex === items.length - 1) {
      return
    }
    if (focusIndex === undefined) {
      itemsRef.current[0]?.focus()
      setFocusIndex(0)
      return
    }
    itemsRef.current[focusIndex]?.focus()
    itemsRef.current[focusIndex + 1]?.focus()
    setFocusIndex(focusIndex + 1)
  }, [focusIndex, items.length])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const { code } = e
      if (open && code === 'Escape') {
        setOpen(false)
        dropdownRef.current?.focus()
        return
      }
      if (!open || (code !== 'ArrowUp' && code !== 'ArrowDown')) {
        return
      }
      e.preventDefault()
      if (code === 'ArrowUp') {
        handleArrowUp()
        return
      }
      handleArrowDown()
    },
    [open, handleArrowUp, handleArrowDown]
  )

  const handleWindowClick = useCallback(
    (e: MouseEvent) => {
      if (
        !menuRef.current ||
        menuRef.current.contains(e.target as Node) ||
        !open
      ) {
        return
      }
      setOpen(false)
      if (focusIndex !== undefined) {
        itemsRef.current[focusIndex]?.focus()
      }
      setFocusIndex(undefined)
    },
    [open, focusIndex]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('click', handleWindowClick)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleWindowClick)
    }
  }, [handleKeyDown, handleWindowClick])

  const onChangeCallback = (item: string) => {
    setOpen(false)
    setFocusIndex(undefined)
    if (focusIndex !== undefined) {
      itemsRef.current[focusIndex]?.focus()
    }
    dropdownRef.current?.focus()
    onChangeHandler(item)
  }

  return (
    <div
      className="DropdownMenu Accordion u-flex u-flexJustifyCenter u-posRelative"
      data-testid={testId || undefined}
    >
      <div className="Content DropdownMenu-content">
        <div className="accordion-target card-summary">
          <span className="Type-2" aria-hidden="true">
            {summaryBeforeText}
          </span>
          <button
            className="Dropdown-toggle Link Link--Trigger u-padding0 u-inlineBlock"
            onClick={e => {
              e.stopPropagation()
              setOpen(!open)
              setFocusIndex(undefined)
            }}
            onMouseDown={e => {
              e.preventDefault()
            }}
            ref={dropdownRef}
            aria-haspopup="menu"
            aria-controls="menu-list"
            aria-expanded={open}
            aria-label={`${summaryBeforeText} ${currentItem} ${summaryAfterText}`}
          >
            {`${currentItem} `}
          </button>{' '}
          <span className="Type-2" aria-hidden="true">
            {summaryAfterText}
          </span>
        </div>
        {open && (
          <ul
            className="Tooltip u-padding0 u-posAbsolute u-margin0"
            ref={menuRef}
            role="menu"
            id="menu-list"
          >
            {items.map((item, index) => {
              return (
                <li
                  className="Content accordion-item u-paddingHoriz8 u-posRelative u-marginBottom0"
                  key={`${item}${index}`}
                  onClick={() => {
                    onChangeCallback(item)
                  }}
                  role="presentation"
                >
                  <button
                    className="Link Link--inlineBlock u-textColorActionBlue u-paddingHoriz8 u-sizeFull"
                    onMouseDown={e => {
                      e.preventDefault()
                    }}
                    ref={el => (itemsRef.current[index] = el)}
                    aria-current={item === currentItem ? 'true' : undefined}
                    role="menuitem"
                  >
                    {item === currentItem ? (
                      <strong>{item}</strong>
                    ) : (
                      <>{item}</>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
